import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Snackbar } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon, PersonAdd as PersonAddIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import { SurfaceCard } from '../ui/SurfaceCard';
import { SegmentedControl } from '../ui/SegmentedControl';
import { IndividualLeaderboard } from './IndividualLeaderboard';
import { ContestParticipantsList } from './ContestParticipantsList';
import { ParticipantActivityDetail } from './ParticipantActivityDetail';
import TeamMemberLeaderboard from './TeamMemberLeaderboard';
import { ContestHeader } from './ContestHeader';
import { ContestMetaTiles } from './ContestMetaTiles';
import { ContestRulesTab } from './ContestRulesTab';
import { ContestTeamsTab } from './ContestTeamsTab';
import { ContestDialogs } from './ContestDialogs';
import { useContestDetailData } from './useContestDetailData';
import { useContestActions } from './useContestActions';
import { useContestStandings } from './useContestStandings';
import { ContestPodium } from './ContestPodium';
import { ContestActivitySyncButton } from './ContestActivitySyncButton';

interface Participant {
  userId: string;
  name: string;
  username: string;
  stravaProfile?: {
    firstname?: string;
    lastname?: string;
    profile?: string;
  };
}

type ContestTab = 'leaderboard' | 'teams' | 'participants' | 'rules';

export const ContestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('contests');
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState<ContestTab>('leaderboard');
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

  const { contest, teams, loading, error, currentUserId, userGroupRole, groupName } = useContestDetailData(id, refreshKey);
  const { standings, loading: standingsLoading } = useContestStandings(contest, refreshKey);

  const hasStarted = contest ? new Date(contest.startAt) <= new Date() : false;
  const hasEnded = contest ? new Date(contest.endAt) <= new Date() : false;
  const canManage = userGroupRole === 'admin';

  const actions = useContestActions({
    contest,
    hasEnded,
    canManage,
    refresh: () => setRefreshKey(k => k + 1),
    navigate,
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contest) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ color: error ? 'var(--danger)' : 'var(--fg)' }}>{error ?? t('detail.notFound')}</Box>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(contest ? `/groups/${contest.groupId}` : '/groups')}>
          {contest ? t('detail.backToGroup') : t('detail.backToGroups')}
        </Button>
      </Box>
    );
  }

  const isTeam = contest.contestType === 'Team';
  const hasRules = !!(contest.minPace || contest.maxPace || contest.minDistance);

  const tabOptions: { value: ContestTab; label: string }[] = [
    { value: 'leaderboard', label: t('detail.tabs.leaderboard') },
    isTeam ? { value: 'teams', label: t('detail.tabs.teams') } : { value: 'participants', label: t('detail.tabs.participants') },
    { value: 'rules', label: t('detail.tabs.rules') },
  ];

  const changeTab = (next: ContestTab) => {
    setTab(next);
    setSelectedParticipant(null);
  };

  // Late joiners can pull in activities from the contest start; results are frozen once it ends.
  const isOnRoster = !!currentUserId && (isTeam
    ? teams.some(team => team.members.some(member => member.userId === currentUserId))
    : (contest.participantIds ?? []).includes(currentUserId));
  const syncAction = isOnRoster && hasStarted && !hasEnded ? (
    <ContestActivitySyncButton contestId={contest._id} onSynced={() => setRefreshKey(k => k + 1)} notify={actions.notify} />
  ) : null;

  const adminAction = isTeam ? (
    <>
      {canManage && teams.length > 0 && (
        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={actions.openImportMembers} disabled={hasEnded}>
          {t('detail.importMembers')}
        </Button>
      )}
      {canManage && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={actions.openCreateTeam} disabled={hasEnded}>
          {t('detail.createTeam')}
        </Button>
      )}
    </>
  ) : !hasEnded && canManage ? (
    <Button variant="contained" startIcon={<PersonAddIcon />} onClick={actions.openAddParticipant}>
      {t('detail.addParticipants')}
    </Button>
  ) : null;

  const renderBody = () => {
    if (tab === 'rules') return <ContestRulesTab contest={contest} />;
    if (tab === 'teams') {
      return (
        <ContestTeamsTab
          teams={teams}
          contestId={contest._id}
          groupId={contest.groupId}
          membersLocked={hasEnded}
          teamsLocked={hasStarted}
          canManage={canManage}
          onAddMember={actions.openAddMember}
          onChanged={() => setRefreshKey(k => k + 1)}
          notify={actions.notify}
        />
      );
    }
    if (tab === 'participants') {
      if (selectedParticipant) {
        return <ParticipantActivityDetail contestId={contest._id} participant={selectedParticipant} onBack={() => setSelectedParticipant(null)} />;
      }
      return (
        <Box sx={{ p: 2 }}>
          <ContestParticipantsList
            contestId={contest._id}
            onParticipantSelect={setSelectedParticipant}
            canManageParticipants={canManage && !hasEnded}
            onParticipantRemoved={() => setRefreshKey(k => k + 1)}
          />
        </Box>
      );
    }
    if (isTeam) return <TeamMemberLeaderboard contestId={contest._id} teams={teams} currentUserId={currentUserId} refreshKey={refreshKey} />;
    if (selectedParticipant) {
      return <ParticipantActivityDetail contestId={contest._id} participant={selectedParticipant} onBack={() => setSelectedParticipant(null)} />;
    }
    return <IndividualLeaderboard contestId={contest._id} currentUserId={currentUserId} refreshKey={refreshKey} onParticipantSelect={setSelectedParticipant} />;
  };

  return (
    <>
      <ContestHeader contest={contest} groupName={groupName} canManage={canManage} onEdit={actions.openEdit} onDelete={actions.openDelete} />
      <ContestMetaTiles contest={contest} teams={teams} currentUserId={currentUserId} standings={standings} standingsLoading={standingsLoading} />
      {hasEnded && <ContestPodium standings={standings} isTeam={isTeam} />}

      {!isTeam && !hasEnded && (
        <Box sx={{ fontSize: 13, color: 'var(--accent-fg)', backgroundColor: 'var(--accent-bg)', borderRadius: '10px', p: '12px 16px', lineHeight: 1.5 }}>
          <Trans
            t={t}
            i18nKey={hasRules ? 'detail.howToParticipateWithRules' : 'detail.howToParticipate'}
            values={{ activity: t(`activityType.${contest.activityType || 'Run'}`).toLowerCase() }}
            components={{ strong: <strong /> }}
          />
        </Box>
      )}

      <SurfaceCard bodyPadding={0}>
        <Box sx={{ p: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
          <SegmentedControl ariaLabel={t('detail.sectionsLabel')} options={tabOptions} value={tab} onChange={changeTab} />
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {tab === 'leaderboard' && <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{t('detail.tapAthleteHint')}</Box>}
            {syncAction}
            {adminAction}
          </Box>
        </Box>
        {renderBody()}
      </SurfaceCard>

      <ContestDialogs contest={contest} teams={teams} actions={actions} />

      <Snackbar open={actions.snackbar.open} autoHideDuration={6000} onClose={actions.closeSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={actions.closeSnackbar} severity={actions.snackbar.severity} sx={{ width: '100%' }}>
          {actions.snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
